import React, { useEffect, useReducer, useState } from 'react';
import Constants from 'expo-constants';
import { get } from '../common/Api';
import uuid from 'react-native-uuid';

const ActionTypes = Object.freeze({
    ADD_DONATION: 0,
    SELECT_GIFT: 1,
    SHOW_GIFT_OPTIONS: 2,
    SHOW_RECURRENCE_OPTIONS: 3,
    SELECT_RECURRENCE: 4,
    HIDE_MODAL: 5,
    SHOW_MODAL: 6,
    UPDATE_DONATION: 7,
    REMOVE_DONATION: 8,
    RESET_CART: 9,
    UPDATE_CART_DETAILS: 10
});

const PaymentMethods = Object.freeze({
    CREDIT: 'credit/debit card',
    ACH: 'bank account'
});

const initialState = {
    cartDetails: {
        id: undefined,
        donations: [],
        paymentInfo: {
            ccType: undefined,
            last4: undefined,
            method: PaymentMethods.CREDIT,
            token: undefined,
            type: undefined
        },
        addressInfo: {
            accepted: false,
            verified: false,
            details: undefined
        },
        emailInfo: {
            verified: false,
            value: undefined
        },
        phoneInfo: {
            verified: false,
            value: undefined
        }
    },
    countries: [],
    funds: [],
    modal: {
        isRecurrence: false,
        options: [],
        selection: -1,
        visible: false
    },
    selectedFund: undefined,
    selectedRecurrence: undefined,
    recurrences: []
};

const GivingContext = React.createContext();

const GivingReducer = (state, action) => {
    const { type, data } = action;

    if (ActionTypes.SHOW_GIFT_OPTIONS === type) {
        return {
            ...state,
            modal: {
                isRecurrence: false,
                options: state.funds,
                visible: true
            }
        };
    } else if (ActionTypes.SHOW_RECURRENCE_OPTIONS === type) {
        return {
            ...state,
            modal: {
                isRecurrence: true,
                options: state.recurrences,
                selection: 0,
                visible: true
            }
        };
    } else if (ActionTypes.SHOW_MODAL === type) {
        return {
            ...state,
            modal: {
                options: data.modalOptions,
                visible: true
            }
        };
    } else if (ActionTypes.HIDE_MODAL === type) {
        return {
            ...state,
            modal: {
                visible: false
            }
        };
    } else if (ActionTypes.ADD_DONATION === type) {
        state.cartDetails.donations.push({
            ...state.funds[0],
            id: uuid.v4(),
            amount: undefined,
            recurringType: 0
        });

        return {
            ...state
        };
    } else if (ActionTypes.SELECT_GIFT === type) {
        return {
            ...state,
            modal: {
                options: state.funds,
                selection: data,
                visible: false
            }
        };
    } else if (ActionTypes.SELECT_RECURRENCE === type) {
        state.cartDetails.donations.giveRecurring.push(state.funds[0]);

        return {
            ...state,
            modal: {
                selection: data,
                visible: false
            },
            selectedRecurrence: data
        };
    } else if (ActionTypes.UPDATE_DONATION === type) {
        let donation = state.cartDetails.donations.find(donation => donation.id === data.id);

        if (!donation) {
            return state;
        }

        const fund = state.funds.find(fund => fund.fundCode === data.fundCode);

        donation.name = data.name || fund?.name;
        donation.amount = parseFloat(data.amount);
        donation.recurringType = parseInt(data.recurrence || 0);
        donation.fundCode = data.fundCode;

        return {
            ...state
        };
    } else if (ActionTypes.REMOVE_DONATION === type) {
        const donationIndex = state.cartDetails.donations.findIndex(donation => donation.id === data);

        if (donationIndex !== -1) {
            state.cartDetails.donations.splice(donationIndex, 1);
        }

        return {
            ...state
        };
    } else if (ActionTypes.RESET_CART === type) {
        state.cartDetails = initialState.cartDetails;

        state.cartDetails.donations = [{ ...state.funds[0], amount: '', recurringType: 0 }];

        return {
            ...state
        };
    } else if (ActionTypes.UPDATE_CART_DETAILS === type) {
        if (data.cartId) {
            state.cartDetails.id = data.cartId;
        }

        if (data.paymentInfo) {
            state.cartDetails.paymentInfo.token = data.paymentInfo.token;
            state.cartDetails.paymentInfo.type = data.paymentInfo.type;
            state.cartDetails.paymentInfo.ccType = data.paymentInfo.ccType;
            state.cartDetails.paymentInfo.last4 = data.paymentInfo.last4;
            state.cartDetails.paymentInfo.method = data.paymentInfo.method;
        }

        if (data.addressInfo) {
            if (typeof data.addressInfo.accepted === 'boolean') {
                state.cartDetails.addressInfo.accepted = data.addressInfo.accepted;
            }

            if (typeof data.addressInfo.verified === 'boolean') {
                state.cartDetails.addressInfo.verified = data.addressInfo.verified;
            }

            state.cartDetails.addressInfo.details = data.addressInfo.details;
        }

        if (data.phoneInfo) {
            if (typeof data.phoneInfo.verified === 'boolean') {
                state.cartDetails.phoneInfo.verified = data.phoneInfo.verified;
            }

            state.cartDetails.phoneInfo.value = data.phoneInfo.value;
        }

        if (data.emailInfo) {
            if (typeof data.emailValidated === 'boolean') {
                state.cartDetails.emailValidated = data.emailValidated;
            }

            state.cartDetails.emailInfo.value = data.emailInfo.value;
        }

        return {
            ...state
        };
    }

    return state;
};

const GivingContextProvider = props => {
    const [state, dispatch] = useReducer(GivingReducer, initialState);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        let unmounted = true;

        const init = async () => {
            const f1OnlineGivingDetails = await get(`${Constants.manifest?.extra?.apiUrl}/F1/GetOnlineGivingDetails?source=website`);

            if (f1OnlineGivingDetails && unmounted) {
                state.funds = f1OnlineGivingDetails.funds?.map(fund => {
                    return {
                        fundCode: fund.fundCode,
                        id: uuid.v4(),
                        name: fund.name
                    };
                });

                const defaultIndex = state.funds.findIndex(el => el.name === f1OnlineGivingDetails.cornerstoneWebDefaultFund);

                if (defaultIndex !== -1) {
                    state.funds.sort((a, b) => {
                        if (a.name === f1OnlineGivingDetails.cornerstoneWebDefaultFund) {
                            return -1;
                        } else if (b.name === f1OnlineGivingDetails.cornerstoneWebDefaultFund) {
                            return 1;
                        } else {
                            return 0;
                        }
                    });
                }

                state.recurrences = f1OnlineGivingDetails.recurrences?.map(recurrence => {
                    return {
                        name: recurrence.name,
                        type: recurrence.type,
                        id: uuid.v4()
                    };
                });

                state.cartDetails.donations = new Array({ ...state.funds[0], recurringType: 0 });
                setInitialized(true);
            }

            return () => {
                unmounted = false;
            };
        };

        if (!initialized) {
            init();
        }
    }, [initialized, state]);

    if (!initialized) {
        return null;
    }

    return <GivingContext.Provider value={{ state, dispatch }}>{props.children}</GivingContext.Provider>;
};

export { ActionTypes, GivingContextProvider, PaymentMethods };

export default GivingContext;
